import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CookieConsent from "../components/CookieConsent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// DEAL ROOM STATUS - CHANGE THIS TO CONTROL ACCESS
// Set to false to re-enable login
// ============================================
const DEAL_ROOM_CLOSED = true;
// ============================================

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("alec.dent@1868capital.co.uk");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    document.title = DEAL_ROOM_CLOSED 
      ? "Jigsaw Deal Room – Currently Closed" 
      : "Jigsaw Deal Room – Login";
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    
    if (DEAL_ROOM_CLOSED) {
      return; // Prevent any submission when closed
    }
    
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );

      if (response.data.ok) {
        const role = response.data.user?.role;
        if (role === "admin") {
          navigate("/admin");
        } else {
          navigate("/deal");
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-white flex items-center justify-center relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/p1p94yug_unsplash-image-ZsPT5__VRHE.webp)',
        }}
      />
      
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/15 via-slate-800/10 to-cyan-900/18" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/8 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(6,182,212,0.12)_0%,_transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(14,165,233,0.10)_0%,_transparent_50%)]" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} />
      
      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4 sm:mx-6 md:mx-8">
        <div className="rounded-3xl bg-white/[0.01] backdrop-blur-sm shadow-[0_8px_50px_0_rgba(0,0,0,0.15),_0_0_0_1px_rgba(255,255,255,0.1)_inset] p-6 sm:p-8 md:p-10 border border-white/15 relative overflow-hidden">
          {/* Minimal tint */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-slate-900/3 via-transparent to-slate-900/2 pointer-events-none" />
          
          {/* Luminous accents */}
          <div className="absolute -top-32 -right-32 w-80 h-80 bg-gradient-to-br from-cyan-400/3 to-transparent rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-gradient-to-tr from-blue-400/3 to-transparent rounded-full blur-3xl pointer-events-none" />
          
          {/* ==================== CLOSED OVERLAY ==================== */}
          {DEAL_ROOM_CLOSED && (
            <div className="absolute inset-0 z-50 rounded-3xl overflow-hidden">
              {/* Frosted glass effect */}
              <div className="absolute inset-0 backdrop-blur-md bg-slate-900/40" />
              
              {/* Animated gradient border */}
              <div className="absolute inset-0 rounded-3xl p-[1px] overflow-hidden">
                <div 
                  className="absolute inset-0 rounded-3xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(100,116,139,0.3) 0%, rgba(71,85,105,0.1) 50%, rgba(100,116,139,0.3) 100%)',
                  }}
                />
              </div>
              
              {/* Content */}
              <div className="relative h-full flex flex-col items-center justify-center p-8 text-center">
                {/* Elegant lock icon */}
                <div className="mb-6 relative">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-slate-700/50 to-slate-800/50 flex items-center justify-center border border-slate-600/30 shadow-2xl">
                    <svg 
                      className="w-9 h-9 text-slate-300" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={1.5} 
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
                      />
                    </svg>
                  </div>
                  {/* Subtle glow */}
                  <div className="absolute inset-0 w-20 h-20 rounded-full bg-slate-400/10 blur-xl" />
                </div>
                
                {/* CLOSED text - Apple-style typography */}
                <h2 
                  className="text-4xl sm:text-5xl font-extralight tracking-[0.25em] text-white/90 mb-4"
                  style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif' }}
                >
                  CLOSED
                </h2>
                
                {/* Subtle divider */}
                <div className="w-16 h-px bg-gradient-to-r from-transparent via-slate-400/40 to-transparent mb-6" />
                
                {/* Subtext */}
                <p className="text-sm text-slate-400 font-light max-w-xs leading-relaxed">
                  The Deal Room is currently not accepting new sessions.
                </p>
                
                {/* Contact link */}
                <a 
                  href="mailto:info@peakassetinvestments.com"
                  className="mt-6 text-xs text-slate-500 hover:text-slate-300 transition-colors duration-300 flex items-center gap-2 group"
                >
                  <span>Contact us for inquiries</span>
                  <svg 
                    className="w-3 h-3 transform group-hover:translate-x-0.5 transition-transform" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          )}
          {/* ==================== END CLOSED OVERLAY ==================== */}
          
          {/* Content */}
          <div className={`relative z-10 ${DEAL_ROOM_CLOSED ? 'pointer-events-none select-none' : ''}`}>
            {/* Jigsaw Logo */}
            <div className="flex justify-center mb-4 sm:mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/7tkopdr2_Jigsaw%20logo.png"
                alt="Jigsaw Conferences"
                className="h-12 sm:h-14 md:h-16 w-auto opacity-95"
              />
            </div>
            
            {/* Header */}
            <div className="mb-6 sm:mb-8 md:mb-10">
              <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">Deal Room</h1>
                  <p className="text-xs sm:text-sm text-cyan-300">Buyer Access Portal</p>
                </div>
                <a 
                  href="https://www.peakassetinvestments.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex-shrink-0 hover:opacity-80 transition-opacity"
                  title="Peak Asset Investments"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/wztt0jsw_RGB_Logo%20Large%20Full%20Colour.svg"
                    alt="Peak Asset Investments"
                    className="h-6 sm:h-7 w-auto"
                  />
                </a>
              </div>
              <div className="h-px bg-gradient-to-r from-cyan-500/40 via-cyan-500/70 to-transparent"></div>
            </div>

            {/* Login Form */}
            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label 
                  htmlFor="email" 
                  className="block text-xs font-medium text-white mb-2"
                >
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  className="w-full rounded-2xl bg-slate-900/40 backdrop-blur-sm border border-white/50 px-4 py-3 text-sm text-white placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/70 focus:bg-slate-900/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                  disabled={DEAL_ROOM_CLOSED}
                  tabIndex={DEAL_ROOM_CLOSED ? -1 : 0}
                />
              </div>

              <div>
                <label 
                  htmlFor="password" 
                  className="block text-xs font-medium text-white mb-2"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  className="w-full rounded-2xl bg-slate-900/40 backdrop-blur-sm border border-white/50 px-4 py-3 text-sm text-white placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/70 focus:bg-slate-900/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  disabled={DEAL_ROOM_CLOSED}
                  tabIndex={DEAL_ROOM_CLOSED ? -1 : 0}
                />
              </div>

              {error && !DEAL_ROOM_CLOSED && (
                <div 
                  data-testid="login-error-message"
                  className="text-xs text-red-200 bg-red-900/50 border border-red-400/50 rounded-xl px-3 py-2"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading || DEAL_ROOM_CLOSED}
                tabIndex={DEAL_ROOM_CLOSED ? -1 : 0}
                className="w-full rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm sm:text-base font-semibold py-3 sm:py-3.5 hover:from-cyan-400 hover:to-blue-400 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg touch-manipulation"
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </form>

            {/* Footer Note */}
            <p className="mt-4 sm:mt-6 text-[10px] sm:text-[11px] text-white leading-relaxed">
              Access is provided for a single designated buyer. If you've received
              these credentials in error, please contact{" "}
              <a 
                href="mailto:info@peakassetinvestments.com" 
                className="text-cyan-300 hover:text-cyan-200 underline transition-colors font-medium break-all"
              >
                info@peakassetinvestments.com
              </a>
              .
            </p>
            
            {/* Company Registration Details */}
            <div className="mt-3 sm:mt-4 text-[8px] sm:text-[9px] text-white/60 leading-relaxed">
              <p>Registered Company: Jigsaw Conferences Ltd</p>
              <p>Company No: 04729836</p>
              <p className="break-words">Registered Office: 3rd Floor, Mayfair, 45 Albemarle St, London W1S 4JL, UK</p>
            </div>
          </div>
        </div>
      </div>
      <CookieConsent />
    </div>
  );
};

export default Login;
